import React, { useState } from "react";
import { parseVideoUrl } from "../../utils/media.js";
import { ExternalLink, AlertCircle, ShieldAlert } from "lucide-react";

export const VideoEmbedPlayer = ({
  url,
  title = "Video Player",
  className = "",
  autoPlay = false
}) => {
  const [hasError, setHasError] = useState(false);
  const videoInfo = parseVideoUrl(url);

  if (!url || !videoInfo.isValid) {
    return (
      <div className={`w-full min-h-[220px] bg-slate-900 text-stone-300 flex flex-col items-center justify-center p-6 text-center rounded-xl ${className}`}>
        <AlertCircle className="w-8 h-8 text-amber-500 mb-2" />
        <span className="text-sm font-semibold text-slate-200">Invalid or Unsupported Video URL</span>
        <span className="text-xs text-slate-400 mt-1 max-w-md">
          Please provide a valid YouTube link (e.g. https://www.youtube.com/watch?v=... or https://youtu.be/...) or Facebook video link (e.g. https://www.facebook.com/.../videos/... or https://fb.watch/...).
        </span>
        {url && (
          <code className="text-[11px] text-amber-300/80 font-mono mt-3 px-2 py-1 bg-black/40 rounded max-w-full break-all">
            {url}
          </code>
        )}
      </div>
    );
  }

  if (videoInfo.platform === "youtube") {
    return (
      <div className={`relative w-full aspect-video bg-black overflow-hidden rounded-xl shadow-md ${className}`}>
        <iframe
          src={`${videoInfo.embedUrl}${autoPlay ? "&autoplay=1" : ""}`}
          title={title}
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
          allowFullScreen
          loading="lazy"
          className="w-full h-full border-0 absolute inset-0"
        />
      </div>
    );
  }

  if (videoInfo.platform === "facebook") {
    return (
      <div className={`relative w-full bg-slate-950 overflow-hidden rounded-xl border border-slate-800 shadow-md flex flex-col ${className}`}>
        <div className="relative w-full aspect-video min-h-[280px] bg-black">
          <iframe
            src={videoInfo.embedUrl}
            title={title}
            className="w-full h-full border-0 absolute inset-0"
            scrolling="no"
            allow="autoplay; clipboard-write; encrypted-media; picture-in-picture; web-share"
            allowFullScreen
          />
        </div>
        <div className="bg-slate-900 px-3.5 py-2.5 flex items-center justify-between text-xs text-slate-300 border-t border-slate-800">
          <div className="flex items-center gap-2">
            <span className="w-5 h-5 rounded-full bg-blue-600 flex items-center justify-center text-white text-[10px] font-bold">
              f
            </span>
            <span className="font-medium text-slate-200">Facebook Video Embed</span>
          </div>
          <a
            href={videoInfo.cleanUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded bg-blue-600 hover:bg-blue-500 text-white font-semibold text-[11px] transition-colors"
          >
            <span>Watch on Facebook</span>
            <ExternalLink className="w-3 h-3" />
          </a>
        </div>
      </div>
    );
  }

  if (videoInfo.platform === "vimeo") {
    return (
      <div className={`relative w-full aspect-video bg-black overflow-hidden rounded-xl shadow-md ${className}`}>
        <iframe
          src={`${videoInfo.embedUrl}?autoplay=${autoPlay ? 1 : 0}`}
          title={title}
          allow="autoplay; fullscreen; picture-in-picture"
          allowFullScreen
          loading="lazy"
          className="w-full h-full border-0 absolute inset-0"
        />
      </div>
    );
  }

  return (
    <div className={`relative w-full aspect-video bg-black flex items-center justify-center rounded-xl overflow-hidden shadow-md ${className}`}>
      {hasError ? (
        <div className="text-center p-6 space-y-3">
          <ShieldAlert className="w-8 h-8 text-amber-400 mx-auto" />
          <p className="text-xs text-slate-300">Video could not be played directly in browser.</p>
          <a
            href={videoInfo.cleanUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold bg-emerald-800 text-white rounded-lg hover:bg-emerald-900 transition-colors"
          >
            <span>Open Video URL in New Tab</span>
            <ExternalLink className="w-3.5 h-3.5" />
          </a>
        </div>
      ) : (
        <video
          controls
          src={videoInfo.embedUrl}
          className="w-full h-full object-contain"
          onError={() => setHasError(true)}
          playsInline
        >
          Your browser does not support HTML5 video playback.
        </video>
      )}
    </div>
  );
};
